import React, { useMemo, useState } from 'react';
import {
  CPL_FORMULA_STEPS,
  CPL_SLIDERS,
  DEFAULT_CPL_INPUTS,
  calculateCplMetrics,
  formatLtv,
  formatMoney,
  formatPercent,
  getInsightState,
  getRomiPresentation,
  getSliderFillPercent,
} from './cplCalculatorLogic';
import { formatTrustedMessage } from '../../utils/htmlSecurity';
import './CplCalculator.css';

const formatSliderValue = (slider, value) => {
  if (slider.format === 'money') {
    return formatMoney(value, 'ru-RU');
  }

  if (slider.format === 'percent') {
    return formatPercent(value);
  }

  return formatLtv(value);
};

const SliderRow = ({ slider, value, onChange }) => {
  const fillPercent = getSliderFillPercent(value, slider.min, slider.max);
  const trackColor = slider.highlight ? 'var(--accent)' : 'var(--text)';

  return (
    <div className="cpl-slider-row">
      <div className="cpl-slider-top">
        <span className={`cpl-slider-label${slider.highlight ? ' cpl-slider-label--hi' : ''}`}>
          {slider.label}
        </span>
        <span className={`cpl-slider-value${slider.highlight ? ' cpl-slider-value--hi' : ''}`}>
          {formatSliderValue(slider, value)}
        </span>
      </div>
      <input
        type="range"
        className={`cpl-slider-input${slider.highlight ? ' cpl-slider-input--accent' : ''}`}
        min={slider.min}
        max={slider.max}
        step={slider.step}
        value={value}
        onChange={(event) => onChange(slider.id, Number(event.target.value))}
        style={{
          background: `linear-gradient(to right, ${trackColor} ${fillPercent}%, var(--surface2) ${fillPercent}%)`,
        }}
      />
      {slider.hintKey ? <p className="cpl-field-hint">{slider.hint}</p> : null}
    </div>
  );
};

const ResultMiniCard = ({ label, value, sub, explain, valueTone }) => (
  <article className="cpl-result-card">
    <div className="cpl-result-label">{label}</div>
    <div className={`cpl-result-value${valueTone ? ` cpl-result-value--${valueTone}` : ''}`}>{value}</div>
    <div className="cpl-result-sub">{sub}</div>
    {explain ? <p className="cpl-mini-explain">{explain}</p> : null}
  </article>
);

const CplCalculator = () => {
  const [inputs, setInputs] = useState(DEFAULT_CPL_INPUTS);
  const locale = 'ru-RU';

  const metrics = useMemo(() => calculateCplMetrics(inputs), [inputs]);
  const romiPresentation = useMemo(
    () =>
      getRomiPresentation({
        actualCpl: inputs.actualCpl,
        cpl: metrics.cpl,
        romiActual: metrics.romiActual,
      }),
    [inputs.actualCpl, metrics.cpl, metrics.romiActual],
  );
  const insight = useMemo(
    () =>
      getInsightState({
        actualCpl: inputs.actualCpl,
        cpl: metrics.cpl,
        crPct: inputs.crPct,
        ltv: inputs.ltv,
        sharePct: inputs.sharePct,
        cac: metrics.cac,
        romiActual: metrics.romiActual,
      }),
    [inputs.actualCpl, inputs.crPct, inputs.ltv, inputs.sharePct, metrics.cac, metrics.cpl, metrics.romiActual],
  );

  const insightMessage = useMemo(
    () => formatTrustedMessage(insight.message, insight.vars || {}),
    [insight],
  );

  const handleInputChange = (field, value) => {
    setInputs((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const romiValue = romiPresentation.isAboveLine
    ? '0% или ниже'
    : `${romiPresentation.displayValue}%`;

  return (
    <div className="cpl-calculator">
      <div className="cpl-main-layout">
        <section className="cpl-card">
          <h2 className="cpl-card-title">{'Параметры бизнеса'}</h2>
          {CPL_SLIDERS.map((slider) => (
            <React.Fragment key={slider.id}>
              <SliderRow
                slider={slider}
                value={inputs[slider.id]}
                onChange={handleInputChange}
              />
              {slider.dividerAfter ? <div className="cpl-divider" /> : null}
            </React.Fragment>
          ))}
        </section>

        <section className="cpl-results-col">
          <article className="cpl-result-hero">
            <div className="cpl-result-hero-label">{'Максимально допустимый CPL — красная линия'}</div>
            <div className="cpl-result-hero-value">{formatMoney(metrics.cpl, locale)}</div>
            <div className="cpl-result-hero-sub">{'дороже — убыток · дешевле — прибыль'}</div>
          </article>

          <div className="cpl-results-mini">
            <ResultMiniCard
              label={'Маржа с клиента'}
              value={formatMoney(metrics.margin, locale)}
              sub={`${inputs.chek} × ${metrics.marginRate}% × ${inputs.ltv}`}
              explain={'Чек × (1 − себестоимость) × число покупок. Реальные деньги которые клиент приносит после всех расходов.'}
            />
            <ResultMiniCard
              label={'CAC — макс. бюджет на 1 клиента'}
              value={formatMoney(metrics.cac, locale)}
              sub={`${metrics.margin} × ${inputs.sharePct}%`}
              explain={'Маржа × доля на привлечение. Потратить больше — клиент не окупится.'}
            />
            <ResultMiniCard
              label={'Заявок на 1 продажу'}
              value={`${metrics.leadsPerSale} ${'шт'}`}
              sub={`конверсия ${inputs.crPct}`}
              explain={'1 ÷ конверсию продаж. При 30% нужно 3.3 заявки чтобы закрыть одну продажу.'}
            />
            <ResultMiniCard
              label={'ROMI при вашем CPL'}
              value={romiValue}
              sub={romiPresentation.status}
              explain={'На каждый вложенный тенге в рекламу. 100% = вернули вдвое больше чем потратили.'}
              valueTone={romiPresentation.tone}
            />
          </div>

          <div
            className={`cpl-insight cpl-insight--${insight.variant}`}
            dangerouslySetInnerHTML={{ __html: insightMessage }}
          />
        </section>
      </div>

      <section className="cpl-formula-panel">
        <h2 className="cpl-card-title">{'Как считается — шаг за шагом'}</h2>
        {CPL_FORMULA_STEPS.map((step) => (
          <article key={step.marker} className="cpl-formula-step">
            <div className="cpl-formula-step-num">{step.marker}</div>
            <div>
              <div className="cpl-formula-step-title">{step.title}</div>
              <div className="cpl-formula-step-formula">{step.formula}</div>
              <div
                className="cpl-formula-step-example"
                dangerouslySetInnerHTML={{
                  __html: step.example,
                }}
              />
            </div>
          </article>
        ))}
      </section>

      <p className="cpl-footer-note">{'Юнит-экономика на минималках · считайте до запуска рекламы'}</p>
    </div>
  );
};

export default CplCalculator;
